import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import DatePicker from '../../components/DatePicker/DatePicker';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

function CreatePurchase() {
    const { purchaseId } = useParams();
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(Boolean(purchaseId));
    const [projects, setProjects] = useState([]);
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        description: '',
        quantity: '',
        rate: '',
        projectId: ''
    });

    const amount = useMemo(() => {
        const quantity = parseFloat(formData.quantity);
        const rate = parseFloat(formData.rate);
        if (Number.isNaN(quantity) || Number.isNaN(rate)) {
            return '';
        }
        return (quantity * rate).toFixed(2);
    }, [formData.quantity, formData.rate]);

    useEffect(() => {
        withLoading(async () => {
            const projectsRes = await api.get('/projects');
            setProjects(projectsRes.data);

            if (purchaseId) {
                const purchaseRes = await api.get(`/purchases/${purchaseId}`);
                setFormData({
                    date: purchaseRes.data.date ? purchaseRes.data.date.split('T')[0] : '',
                    description: purchaseRes.data.description || '',
                    quantity: String(purchaseRes.data.quantity ?? ''),
                    rate: String(purchaseRes.data.rate ?? ''),
                    projectId: purchaseRes.data.projectId ? String(purchaseRes.data.projectId) : ''
                });
            }
        }).catch((error) => {
            const message = error.response?.data?.Error || 'Error loading purchase data';
            alert(message);
        });
    }, [purchaseId, withLoading]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const buildPayload = () => ({
        date: formData.date,
        description: formData.description.trim(),
        quantity: parseFloat(formData.quantity),
        rate: parseFloat(formData.rate),
        projectId: formData.projectId ? Number(formData.projectId) : null
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = buildPayload();
        const apiCall = purchaseId
            ? api.put(`/purchases/${purchaseId}`, payload)
            : api.post('/purchases', payload);

        apiCall
            .then(() => {
                navigate('/financials/purchase');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error saving purchase';
                alert(message);
            });
    };

    const handleDelete = () => {
        if (!window.confirm('Are you sure you want to delete this purchase entry?')) return;

        api.delete(`/purchases/${purchaseId}`)
            .then(() => {
                navigate('/financials/purchase');
            })
            .catch((error) => {
                const message = error.response?.data?.Error || 'Error deleting purchase';
                alert(message);
            });
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{purchaseId ? 'Modify purchase' : 'Add purchase'}</h1>
                        <p className="form-page-subtitle">
                            {purchaseId
                                ? 'Update purchase details'
                                : 'Record a new purchase entry'}
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/financials/purchase')}
                        >
                            Back to list
                        </button>
                        {purchaseId && (
                            <button
                                type="button"
                                className="form-page-btn-danger"
                                onClick={handleDelete}
                            >
                                Delete purchase
                            </button>
                        )}
                        <button
                            type="submit"
                            form="create-purchase-form"
                            className="form-page-btn"
                        >
                            {purchaseId ? 'Update purchase' : 'Save purchase'}
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Purchase details</h2>
                            <p className="form-page-section-hint">Date, description, quantity, rate, and project</p>
                        </div>
                    </div>

                    <form id="create-purchase-form" onSubmit={handleSubmit}>
                        <div className="form-page-grid cols-3">
                            <div className="form-page-field">
                                <label htmlFor="date">Date</label>
                                <DatePicker
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="quantity">Quantity</label>
                                <input
                                    type="number"
                                    id="quantity"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleChange}
                                    required
                                    step="0.01"
                                    min="0.01"
                                    placeholder="0"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="rate">Rate</label>
                                <input
                                    type="number"
                                    id="rate"
                                    name="rate"
                                    value={formData.rate}
                                    onChange={handleChange}
                                    required
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="amount">Amount</label>
                                <input
                                    type="text"
                                    id="amount"
                                    name="amount"
                                    value={amount}
                                    readOnly
                                    placeholder="0.00"
                                    aria-readonly="true"
                                />
                            </div>
                            <div className="form-page-field">
                                <label htmlFor="projectId">Project (optional)</label>
                                <select
                                    id="projectId"
                                    name="projectId"
                                    value={formData.projectId}
                                    onChange={handleChange}
                                >
                                    <option value="">No project</option>
                                    {projects.map((project) => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                            {project.clientName ? ` (${project.clientName})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-page-field span-full">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    required
                                    placeholder="Enter purchase description"
                                />
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </PageLoadingArea>
    );
}

export default CreatePurchase;
